import type { DocumentUpdateSummary, AdminDocumentRequirementsPage, AdminDocumentRequirementsItem, DocumentPortalFilter, DocumentStatusFilter } from "@ohf/core";
import { useQuery } from "@tanstack/react-query";
import { useMe } from "../../auth/MeProvider.js";
import { useApi } from "../../auth/SessionProvider.js";
import type { Me } from "../../lib/me.js";

export function documentIdentity(me: Me | null) {
  return JSON.stringify([me?.userId, me?.authUserId, me?.email, me?.party?.id, me?.role, me?.capabilities, me?.scope, me?.viewAs]);
}

export function useDocumentUpdates() {
  const api = useApi();
  const { me, mfaBlocked } = useMe();
  return useQuery({
    queryKey: ["document-updates", documentIdentity(me)],
    queryFn: ({ signal }) => api.get<DocumentUpdateSummary>("/document-updates", signal),
    enabled: Boolean(me?.userId && me.authUserId && me.party?.id && me.scope && !mfaBlocked),
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useAdminDocumentRequirements(search = "", offset = 0, limit = 50, portal: DocumentPortalFilter = "all", status: DocumentStatusFilter = "outstanding") {
  const api = useApi();
  const { me, mfaBlocked } = useMe();
  const allowed = Boolean(me?.role === "admin" && !me.viewAs && me.userId && me.authUserId && me.scope && !mfaBlocked);
  return useQuery({
    queryKey: ["admin-document-requirements", documentIdentity(me), search, offset, limit, portal, status],
    queryFn: ({ signal }) => api.get<AdminDocumentRequirementsPage>(`/document-updates/admin?${new URLSearchParams({ search, offset: String(offset), limit: String(limit), portal, status })}`, signal),
    enabled: allowed, retry: false, staleTime: 30_000, refetchOnMount: "always",
  });
}

export function useAdminDocumentDetail(partyId: string, available = true) {
  const api = useApi();
  const { me, mfaBlocked } = useMe();
  return useQuery({
    queryKey: ["admin-document-detail", documentIdentity(me), partyId],
    queryFn: ({ signal }) => api.get<AdminDocumentRequirementsItem>(`/document-updates/admin/${encodeURIComponent(partyId)}`, signal),
    enabled: Boolean(available && partyId && me?.role === "admin" && !me.viewAs && me.userId && me.authUserId && me.scope && !mfaBlocked),
    retry: false, staleTime: 0, gcTime: 0, refetchOnMount: "always",
  });
}
